import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import {
  catchError,
  combineLatest,
  filter,
  map,
  mergeMap,
  of,
  switchMap
} from 'rxjs';
import {
  HealthTrackerUserMetricsTimelineMetricTimeline,
  HealthTrackerUserMetricsTimelineRequestParams
} from '@providers/api/health-tracker/health-tracker.interface';
import { ApiHealthTrackerService } from '@providers/api/health-tracker/health-tracker.service';
import { TrackerDetailActions } from '../actions';
import { SLEEP_SCORE_CONFIG } from '../sleep-score/sleep-score.config';
import {
  TrackerDetailAnalyticsQuery,
  TrackerDetailTodayQuery,
  TrackerDetailTodayQueryData
} from '../states/tracker-detail.state.interface';
import { HealthTrackerDataType } from '../tracker.data-types.interface';
import { selectTrackerActive } from '../tracker.selectors';
import { TrackerService } from '../tracker.service';

/**
 * @description Tracker Detail Effects.
 * @exports
 * @class TrackerDetailEffects
 */
@Injectable()
export class TrackerDetailEffects extends TrackerService {
  private readonly actions = inject(Actions);
  private readonly healthTrackerService = inject(ApiHealthTrackerService);
  private readonly store = inject(Store);

  readonly fetchDetailAnalyticsQuery = createEffect(() => {
    return this.actions.pipe(
      ofType(TrackerDetailActions.fetchQueriesCollection),
      filter((action) => !action.queryType.endsWith('-today')),
      mergeMap((action) => {
        const activeTracker = this.store.selectSignal(selectTrackerActive)();
        const now = DateTime.now();
        let start = now.startOf('day');
        const end = now.endOf('day');

        const metricType = action.queryType
          .split('-analytics-')[0]
          .toUpperCase();

        let timeFrame: HealthTrackerUserMetricsTimelineRequestParams['timeFrame'] =
          'hour';
        if (action.queryType.includes('day')) {
          if (metricType === (HealthTrackerDataType.Sleep as any as string)) {
            start = now.startOf('day').minus({
              hours: SLEEP_SCORE_CONFIG.HOURS_OFFSET_FOR_SLEEP_START
            });
          } else {
            start = now.startOf('day');
          }
          timeFrame = 'hour';
        } else if (action.queryType.includes('week')) {
          timeFrame = 'day';
          start = now.startOf('day').minus({ days: 6 });
        } else if (action.queryType.includes('month')) {
          timeFrame = 'day';
          start = now.startOf('day').minus({ days: 27 });
        } else {
          timeFrame = 'month';
          start = now.startOf('month').minus({ months: 11 });
        }
        const timezoneOffsetMinutes = -new Date().getTimezoneOffset();

        return combineLatest({
          metricQuery: this.healthTrackerService.getUserRecordsMetricsTimeline({
            startDate: start.toUTC().toISO(),
            endDate: end.toUTC().toISO(),
            metrics: [metricType],
            timeFrame: timeFrame,
            wearableCode: activeTracker?.tracker.code ?? '',
            includeScore: ['avg', 'total'],
            timezoneOffsetMinutes
          }),
          metricDetailsQuery:
            metricType === 'SLEEP' || metricType === 'BLOOD_PRESSURE'
              ? this.healthTrackerService.getUserRecordsMetricsDetailsCategory(
                  {
                    categoryCode: metricType
                  },
                  {
                    startDate: start.toUTC().toISO(),
                    endDate: end.toUTC().toISO(),
                    wearableCode: activeTracker?.tracker.code ?? ''
                  }
                )
              : of(null)
        }).pipe(
          map(({ metricQuery, metricDetailsQuery }) => {
            const metric = metricQuery.data?.metrics.find(
              (metric) => metric.code === metricType
            );
            let timeline: TrackerDetailAnalyticsQuery['data']['metricTimeline'] =
              [];

            const otherMetrics: Array<{
              type: 'max' | 'min';
              timeline: HealthTrackerUserMetricsTimelineMetricTimeline;
            }> = [];
            if (metric?.timeline && Array.isArray(metric.timeline)) {
              const timelineMax = {
                score: 0,
                index: -1
              };
              const timelineMin = {
                score: undefined as number | undefined,
                index: -1
              };
              metric.timeline.forEach((item, index) => {
                timeline.push({
                  endDate: item.endDate,
                  startDate: item.startDate,
                  scores: item.score
                });
                if (item.score[0].value > timelineMax.score) {
                  timelineMax.score = item.score[0].value;
                  timelineMax.index = index;
                }
                if (
                  timelineMin.score === undefined ||
                  item.score[0].value < timelineMin.score
                ) {
                  timelineMin.score = item.score[0].value;
                  timelineMin.index = index;
                }
              });
              timelineMin.score = timelineMin.score ?? 0;
              if (timelineMax.index !== -1) {
                otherMetrics.push({
                  type: 'max',
                  timeline: metric.timeline[timelineMax.index]
                });
              }
              if (timelineMin.index !== -1) {
                otherMetrics.push({
                  type: 'min',
                  timeline: metric.timeline[timelineMin.index]
                });
              }
            }
            let scores: TrackerDetailAnalyticsQuery['data']['metricDetailScore'] =
              [];
            if (Array.isArray(metric?.includeScore)) {
              metric.includeScore.forEach((scoreItem) => {
                scores.push({
                  type: scoreItem.scoreFormula,
                  score: scoreItem.score,
                  code: scoreItem.code
                });
              });
            }
            if (metric) {
              scores.push({
                type: metric.scoreFormula,
                score: metric.score,
                scoreUnit: metric.scoreUnit,
                startDate: '',
                endDate: '',
                scoreFormula: metric.scoreFormula
              });
            }
            for (const otherMetric of otherMetrics) {
              scores.push({
                type: otherMetric.type,
                score: otherMetric.timeline.score[0].value,
                scoreUnit: metric?.scoreUnit,
                startDate: otherMetric.timeline.startDate,
                endDate: otherMetric.timeline.endDate
              });
            }
            if (metricDetailsQuery?.data) {
              scores.push({
                type: `detail-${metricDetailsQuery.data.scoreFormula}`,
                score: metricDetailsQuery.data.score,
                scoreUnit: metricDetailsQuery.data.scoreUnit,
                startDate: metricDetailsQuery.data.startDate,
                endDate: metricDetailsQuery.data.endDate
              });
              if (Array.isArray(metricDetailsQuery.data.mappings)) {
                for (const mapping of metricDetailsQuery.data.mappings) {
                  scores.push({
                    type: mapping.code,
                    score: mapping.score,
                    scoreUnit: mapping.scoreUnit,
                    startDate:
                      mapping.startTimestamp ??
                      metricDetailsQuery.data.startDate,
                    endDate:
                      mapping.endTimestamp ?? metricDetailsQuery.data.endDate
                  });
                }
              }
            }

            scores = scores.map((score) => ({
              ...score,
              startDate:
                DateTime.fromISO(score.startDate ?? '')
                  .toUTC()
                  .toISO() ?? '',
              endDate:
                DateTime.fromISO(score.endDate ?? '')
                  .toUTC()
                  .toISO() ?? ''
            }));
            timeline = timeline.map((item) => ({
              ...item,
              startDate: DateTime.fromISO(item.startDate).toUTC().toISO() ?? '',
              endDate: DateTime.fromISO(item.endDate).toUTC().toISO() ?? ''
            }));

            return TrackerDetailActions.fetchDetailAnalyticsSuccess({
              data: {
                metricDetailScore: scores,
                metricTimeline: timeline,
                predictiveData: metricDetailsQuery?.data?.predictiveData ?? {
                  predictiveInsight: '',
                  predictiveLevel: ''
                }
              },
              queryType: action.queryType as TrackerDetailAnalyticsQuery['type']
            });
          }),
          catchError((error: unknown) => {
            return of(
              TrackerDetailActions.fetchQueriesCollectionFailed({
                queryType: action.queryType,
                error: (error as Error).message
              })
            );
          })
        );
      })
    );
  });

  readonly fetchDetailToday = createEffect(() => {
    return this.actions.pipe(
      ofType(TrackerDetailActions.fetchQueriesCollection),
      filter((action) => action.queryType.endsWith('-today')),
      switchMap((action) => {
        const activeTracker = this.store.selectSignal(selectTrackerActive)();

        const metricType = action.queryType.split('-today')[0].toUpperCase();

        const timezoneOffsetMinutes = -new Date().getTimezoneOffset();

        const now = DateTime.now();

        let startDate = now.startOf('day');

        if (metricType === (HealthTrackerDataType.Sleep as string)) {
          startDate = startDate.minus({
            hours: SLEEP_SCORE_CONFIG.HOURS_OFFSET_FOR_SLEEP_START
          });
        }

        return this.healthTrackerService
          .getUserRecordsMetricsTimeline({
            startDate: startDate.toUTC().toISO(),
            endDate: now.endOf('day').toUTC().toISO(),
            metrics: [metricType],
            timeFrame: 'hour',
            wearableCode: activeTracker?.tracker.code ?? '',
            includeScore: ['avg', 'total', 'latest'],
            timezoneOffsetMinutes
          })
          .pipe(
            map((response) => {
              const metric = response.data?.metrics.find(
                (metric) => metric.code === metricType
              );
              let max = 0;
              let min = 0;
              let avgValue = 0;
              let latestValue = 0;
              if (metric?.timeline && Array.isArray(metric.timeline)) {
                let maxValue = Math.max(
                  ...metric.timeline.map((item) => item.score[0].value)
                );
                max = Number.isFinite(maxValue) ? maxValue : 0;
                let minValue = Math.min(
                  ...metric.timeline.map((item) => item.score[0].value)
                );
                min = Number.isFinite(minValue) ? minValue : 0;
              }

              let scores: TrackerDetailTodayQueryData['scores'] = [];
              if (Array.isArray(metric?.includeScore)) {
                const avgScore = metric.includeScore.find(
                  (score) => score.scoreFormula === 'avg'
                );
                if (avgScore) {
                  avgValue = avgScore.score;
                }
                const latestScore = metric.includeScore.find(
                  (score) => score.scoreFormula === 'latest'
                );
                if (latestScore) {
                  latestValue = latestScore.score;
                }
                metric.includeScore.forEach((scoreItem) => {
                  scores.push({
                    score: scoreItem.score,
                    scoreFormula: scoreItem.scoreFormula,
                    type: scoreItem.scoreFormula,
                    code: scoreItem.code,
                    scoreUnit: scoreItem.scoreUnit
                  });
                });
              }

              scores.push(
                ...[
                  {
                    type: metric?.scoreFormula ?? '',
                    score: metric?.score ?? 0,
                    scoreFormula: metric?.scoreFormula ?? '',
                    scoreUnit: metric?.scoreUnit ?? ''
                  },
                  {
                    type: 'avg',
                    score: avgValue,
                    scoreFormula: 'avg',
                    scoreUnit: metric?.scoreUnit ?? ''
                  },
                  {
                    type: 'max',
                    score: max,
                    scoreFormula: 'max',
                    scoreUnit: metric?.scoreUnit ?? ''
                  },
                  {
                    type: 'min',
                    score: min,
                    scoreFormula: 'min',
                    scoreUnit: metric?.scoreUnit ?? ''
                  },
                  {
                    type: 'latest',
                    score: latestValue,
                    scoreFormula: 'latest',
                    scoreUnit: metric?.scoreUnit ?? ''
                  }
                ]
              );

              if (metricType === 'BLOOD_PRESSURE') {
                if (Array.isArray(metric?.includeScore)) {
                  const dataAvg = metric.includeScore.filter(
                    (val) => val.scoreFormula === 'avg'
                  );
                  const dataLatest = metric.includeScore.filter(
                    (val) => val.scoreFormula === 'latest'
                  );

                  if (dataAvg.length > 0) {
                    const systolicAvg = dataAvg.find(
                      (val) => val.code === 'blood_pressure_systolic'
                    );
                    const diastolicAvg = dataAvg.find(
                      (val) => val.code === 'blood_pressure_diastolic'
                    );

                    if (systolicAvg || diastolicAvg) {
                      const systolicAvgValue = systolicAvg
                        ? systolicAvg.score
                        : 0;
                      const diastolicAvgValue = diastolicAvg
                        ? diastolicAvg.score
                        : 0;
                      scores.push(
                        {
                          type: 'blood_pressure_systolic',
                          code: 'blood_pressure_systolic',
                          score: systolicAvgValue,
                          scoreFormula: 'avg',
                          scoreUnit: metric.scoreUnit
                        },
                        {
                          type: 'blood_pressure_diastolic',
                          code: 'blood_pressure_diastolic',
                          score: diastolicAvgValue,
                          scoreFormula: 'avg',
                          scoreUnit: metric.scoreUnit
                        }
                      );
                    }
                  }

                  if (dataLatest.length > 0) {
                    const systolicLatest = dataLatest.find(
                      (val) => val.code === 'blood_pressure_systolic'
                    );
                    const diastolicLatest = dataLatest.find(
                      (val) => val.code === 'blood_pressure_diastolic'
                    );

                    if (systolicLatest || diastolicLatest) {
                      const systolicLatestValue = systolicLatest
                        ? systolicLatest.score
                        : 0;
                      const diastolicLatestValue = diastolicLatest
                        ? diastolicLatest.score
                        : 0;
                      scores.push(
                        {
                          type: 'blood_pressure_systolic',
                          code: 'blood_pressure_systolic',
                          score: systolicLatestValue,
                          scoreFormula: 'latest',
                          scoreUnit: metric.scoreUnit
                        },
                        {
                          type: 'blood_pressure_diastolic',
                          code: 'blood_pressure_diastolic',
                          score: diastolicLatestValue,
                          scoreFormula: 'latest',
                          scoreUnit: metric.scoreUnit
                        }
                      );
                    }
                  }
                }
              }

              return TrackerDetailActions.fetchDetailTodaySuccess({
                data: { scores },
                queryType: action.queryType as TrackerDetailTodayQuery['type']
              });
            }),
            catchError((error: unknown) => {
              return of(
                TrackerDetailActions.fetchQueriesCollectionFailed({
                  queryType: action.queryType,
                  error: (error as Error).message
                })
              );
            })
          );
      })
    );
  });
}
